/**
 * TaskPane.tsx
 * Root task pane shell component. Renders the sidebar nav
 * and the active panel based on `initialPanel` prop (set by route).
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
  LayoutGrid, ListChecks, Cloud, MessageSquare, Pencil, Upload,
  Users, Trophy, Sparkles, Play, QrCode, BarChart3, Video, Copy, Check, Link2,
} from 'lucide-react';
import { useAppStore } from '@store/appStore';
import { useTeacherAuth } from '@hooks/useTeacherAuth';
import { useAutoFollow } from '@hooks/useAutoFollow';
import { signOutTeacher } from '@db/firebase';
import { createRoom, closeRoom } from '@db/roomService';
import { saveActiveSession, clearActiveSession } from '@office/settingsService';
import Button from '@components/shared/Button';
import LoginScreen from './LoginScreen';
import LoadingSpinner from '@components/shared/LoadingSpinner';
import UpgradeGate from '@components/shared/UpgradeGate';
import { toolAllowed, TIER_LIMITS } from '@utils/entitlements';
import MultipleChoice from './QuizBuilder/MultipleChoice';
import WordCloud from './QuizBuilder/WordCloud';
import ShortAnswer from './QuizBuilder/ShortAnswer';
import SlideDrawing from './QuizBuilder/SlideDrawing';
import ClassRoster from './ClassManager/ClassRoster';
import Leaderboard from './ClassManager/Leaderboard';
import AIQuizBuilder from './AIBuilder/AIQuizBuilder';
import HomePanel from './HomePanel';
import LiveResults from './LiveResults';
import VideoQuiz from './QuizBuilder/VideoQuiz';
import VideoPresenter from './VideoPresenter';
import MediaUpload from './QuizBuilder/MediaUpload';

export type PanelId =
  | 'home' | 'mc' | 'wordcloud' | 'shortanswer' | 'drawing'
  | 'media' | 'class' | 'leaderboard' | 'ai' | 'results' | 'video';

interface TaskPaneProps {
  initialPanel?: PanelId;
}

const NAV_ITEMS = [
  { id: 'home' as PanelId,        label: 'Home',          icon: LayoutGrid,    color: '#5660F7' },
  { id: 'mc' as PanelId,          label: 'Multiple Choice', icon: ListChecks,  color: '#5660F7' },
  { id: 'wordcloud' as PanelId,   label: 'Word Cloud',    icon: Cloud,         color: '#FF820A' },
  { id: 'shortanswer' as PanelId, label: 'Short Answer',  icon: MessageSquare, color: '#22C55E' },
  { id: 'drawing' as PanelId,     label: 'Drawing',       icon: Pencil,        color: '#A855F7' },
  { id: 'video' as PanelId,       label: 'Video',         icon: Video,         color: '#EF4444' },
  { id: 'media' as PanelId,       label: 'Media',         icon: Upload,        color: '#3B82F6' },
  { id: 'class' as PanelId,       label: 'My Class',      icon: Users,         color: '#3B82F6' },
  { id: 'leaderboard' as PanelId, label: 'Leaderboard',   icon: Trophy,        color: '#EAB308' },
  { id: 'ai' as PanelId,          label: 'AI Builder',    icon: Sparkles,      color: '#A855F7' },
];

export default function TaskPane({
  initialPanel = 'home',
}: TaskPaneProps): React.ReactElement {
  const { user: teacher, loading: authLoading } = useTeacherAuth();
  const [activePanel, setActivePanel] = useState<PanelId>(initialPanel);
  const [startingRoom, setStartingRoom] = useState(false);
  const [copied, setCopied] = useState(false);
  const activeRoom = useAppStore((s) => s.activeRoom);
  const setActiveRoom = useAppStore((s) => s.setActiveRoom);
  const activeClassId = useAppStore((s) => s.activeClassId);
  const tier = useAppStore((s) => s.tier);

  // Sync panel when ribbon re-opens task pane to a different route
  useEffect(() => {
    setActivePanel(initialPanel);
  }, [initialPanel]);

  // After login, if no class is active yet, open the class picker so the
  // teacher selects a saved class before doing anything else.
  useEffect(() => {
    if (teacher && !activeClassId) setActivePanel('class');
  }, [teacher, activeClassId]);

  // When an activity goes live (pushed by "Start Presenting"), jump to the
  // Results tab so the teacher immediately sees incoming answers. Works for
  // any activity type (question for MC/short answer, prompt for the rest).
  const cfg = activeRoom?.status === 'active' ? activeRoom.activeConfig : null;
  const liveActivity = cfg
    ? (cfg as { question?: string; prompt?: string }).question ??
      (cfg as { prompt?: string }).prompt ?? null
    : null;
  // Track the current panel without retriggering the auto-nav effect, so we
  // can suppress the jump-to-Results while the teacher is driving a video quiz
  // from the Video panel (that panel shows results inline).
  const activePanelRef = useRef(activePanel);
  activePanelRef.current = activePanel;
  useEffect(() => {
    if (liveActivity && activePanelRef.current !== 'video') setActivePanel('results');
  }, [liveActivity]);

  // Auto-follow: while a session is live, students track the teacher's
  // current slide automatically. Slide-image mirroring is Premium+ only.
  useAutoFollow(activeRoom?.roomCode ?? null, !!activeRoom, TIER_LIMITS[tier].slideMirror);

  const handleStartRoom = async () => {
    if (!activeClassId) {
      setActivePanel('class');
      return;
    }
    setStartingRoom(true);
    try {
      const roomCode = await createRoom(activeClassId, 'GuroHub Session');
      // Re-subscribe via store
      setActiveRoom({ roomCode } as never);
      // Persist to document settings so the ribbon "Start Presenting"
      // command runtime can read the live room code.
      await saveActiveSession({ roomCode, classId: activeClassId });
    } catch (err) {
      console.error('Failed to create room:', err);
    } finally {
      setStartingRoom(false);
    }
  };

  const handleEndRoom = async () => {
    const roomCode = activeRoom?.roomCode;
    // Tear down locally first so the UI leaves the live state immediately,
    // then best-effort close the room + clear the persisted session. Each
    // remote step is isolated so a failure in one still runs the other and
    // never leaves a stale session to be restored on the next reload.
    setActiveRoom(null);
    if (roomCode) {
      try { await closeRoom(roomCode); }
      catch (err) { console.error('[GuroHub] closeRoom failed:', err); }
    }
    try { await clearActiveSession(); }
    catch (err) { console.error('[GuroHub] clearActiveSession failed:', err); }
  };

  const sessionLive =
    activeRoom?.status === 'active' || activeRoom?.status === 'waiting' || activeRoom?.status === 'results';

  // Student join link for the live room. VITE_APP_URL is localhost in dev and
  // https://app.gurohub.com in production; the code prefills the join form.
  const joinBase = `${import.meta.env.VITE_APP_URL}/#/join`;
  const joinUrl = activeRoom?.roomCode ? `${joinBase}?code=${activeRoom.roomCode}` : joinBase;
  const copyJoin = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (err) {
      console.warn('[GuroHub] Clipboard copy failed:', err);
    }
  };

  // The Results tab is only relevant while a session is live.
  const navItems = sessionLive
    ? [
        NAV_ITEMS[0],
        { id: 'results' as PanelId, label: 'Results', icon: BarChart3, color: '#EC4899' },
        ...NAV_ITEMS.slice(1),
      ]
    : NAV_ITEMS;

  const renderPanel = () => {
    // Premium-only quiz tools (free tier gets Multiple Choice only).
    if (activePanel === 'wordcloud' && !toolAllowed(tier, 'word_cloud'))
      return <UpgradeGate feature="Word Cloud" requiredTier="premium" />;
    if (activePanel === 'shortanswer' && !toolAllowed(tier, 'short_answer'))
      return <UpgradeGate feature="Short Answer" requiredTier="premium" />;
    if (activePanel === 'drawing' && !toolAllowed(tier, 'slide_drawing'))
      return <UpgradeGate feature="Slide Drawing" requiredTier="premium" />;
    if (activePanel === 'video' && !toolAllowed(tier, 'video_quiz'))
      return <UpgradeGate feature="Interactive Video" requiredTier="premium" />;
    if (activePanel === 'media' && !toolAllowed(tier, 'media_upload'))
      return <UpgradeGate feature="Media Upload" requiredTier="premium" />;

    switch (activePanel) {
      case 'mc':          return <MultipleChoice />;
      case 'wordcloud':   return <WordCloud />;
      case 'shortanswer': return <ShortAnswer />;
      case 'drawing':     return <SlideDrawing />;
      // While live, the Video tab becomes the tap-to-ask presenter; otherwise
      // it's the builder for authoring timestamped questions.
      case 'video':       return sessionLive ? <VideoPresenter /> : <VideoQuiz />;
      case 'media':       return <MediaUpload />;
      case 'class':       return <ClassRoster />;
      case 'leaderboard': return <Leaderboard />;
      case 'ai':          return <AIQuizBuilder />;
      case 'results':     return <LiveResults />;
      default:            return <HomePanel onNavigate={setActivePanel} />;
    }
  };

  // Gate the teacher UI behind login so classes/data persist per account.
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-surface">
        <LoadingSpinner size="lg" label="Loading…" />
      </div>
    );
  }
  if (!teacher) {
    return <LoginScreen />;
  }

  return (
    <div className="flex flex-col h-full bg-surface overflow-hidden">
      {/* ---- Brand header ---------------------------------- */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-surface-border">
        <img
          src="/assets/icons/guro_ai.gif"
          alt="GuroHub"
          className="w-8 h-8 rounded-full object-cover border-2 border-brand-500"
        />
        <div>
          <h1 className="text-sm font-bold text-slate-900 leading-tight">GuroHub</h1>
          <p className="text-[10px] text-slate-500">Interactive Classroom</p>
        </div>

        {/* Sign out */}
        <button
          onClick={() => { void signOutTeacher(); }}
          className="ml-auto text-[10px] text-slate-500 hover:text-slate-700 transition-colors"
          title={teacher.email ?? 'Sign out'}
        >
          Sign out
        </button>

        {/* Room code badge */}
        {activeRoom?.roomCode && (
          <div className="ml-auto flex items-center gap-1.5 bg-surface-elevated border border-surface-border rounded-lg px-2 py-1">
            <QrCode size={12} className="text-brand-400" />
            <span className="text-xs font-mono font-bold text-brand-400">
              {activeRoom.roomCode}
            </span>
          </div>
        )}
      </div>

      {/* ---- Nav tabs --------------------------------------- */}
      <div className="flex gap-1 px-3 py-2 border-b border-surface-border overflow-x-auto scrollbar-hide">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = activePanel === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setActivePanel(item.id)}
              className={clsx(
                'flex-shrink-0 flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded-lg',
                'text-[10px] font-medium transition-all duration-150 border',
                isActive
                  ? 'font-semibold'
                  : 'border-transparent text-slate-500 hover:bg-surface-elevated',
              )}
              style={
                isActive
                  ? { backgroundColor: `${item.color}1A`, borderColor: `${item.color}55`, color: item.color }
                  : undefined
              }
            >
              <Icon size={14} color={item.color} />
              {item.label}
            </button>
          );
        })}
      </div>

      {/* ---- Panel content ---------------------------------- */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activePanel}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="h-full"
          >
            {renderPanel()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ---- Start/Stop room CTA --------------------------- */}
      <div className="px-3 py-3 border-t border-surface-border">
        {activeRoom?.status === 'active' || activeRoom?.status === 'waiting' || activeRoom?.status === 'results' ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-brand-500/10 border border-brand-500/30 rounded-xl px-3 py-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                <span className="text-[10px] font-bold text-green-600 uppercase tracking-wide">Live</span>
                <span className="ml-auto text-lg font-mono font-extrabold text-brand-600 tracking-widest tabular-nums">
                  {activeRoom.roomCode}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEndRoom}
                className="text-red-500 hover:text-red-600"
              >
                End
              </Button>
            </div>
            {/* Copyable student join link */}
            <button
              onClick={copyJoin}
              title="Copy join link"
              className="w-full flex items-center gap-2 bg-surface-elevated border border-surface-border rounded-lg px-3 py-1.5 text-left hover:border-brand-500/40 transition-colors"
            >
              <Link2 size={12} className="text-slate-400 shrink-0" />
              <span className="text-[11px] text-slate-600 truncate flex-1">{joinUrl}</span>
              {copied
                ? <Check size={13} className="text-green-500 shrink-0" />
                : <Copy size={13} className="text-slate-400 shrink-0" />}
            </button>
          </div>
        ) : (
          <Button
            fullWidth
            icon={<Play size={14} />}
            loading={startingRoom}
            onClick={handleStartRoom}
          >
            Start Class Session
          </Button>
        )}
      </div>
    </div>
  );
}
